import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { EndpointStats } from '../../types';
import {
  CHART_AXIS_PROPS,
  CHART_AXIS_TICK,
  CHART_BAR_PROPS,
  CHART_ERROR,
  CHART_GRID_PROPS,
  CHART_SUCCESS,
  CHART_SUCCESS_LIGHT,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_TOOLTIP_STYLE,
} from './chartTheme';
import {
  ChartBarGradient,
  ChartEmptyState,
  ChartPanel,
} from './ChartPrimitives';
import { chartGradientFill, useChartGradientId } from './chartGradient';

interface EndpointSuccessChartProps {
  stats: EndpointStats[];
  title?: string;
}

function shortenEndpoint(endpoint: string, provider: 'fal' | 'gemini'): string {
  const prefix = provider === 'fal' ? 'fal' : 'gemini';
  const name = endpoint.replace(/^fal-ai\//, '').replace(/-preview$/, '');
  return `${prefix}: ${name}`;
}

export function EndpointSuccessChart({ stats, title = 'Endpoint Success Rate' }: EndpointSuccessChartProps) {
  const completedGradientId = useChartGradientId('endpoint-completed');
  const failedGradientId = useChartGradientId('endpoint-failed');
  const data = stats.map((s) => ({
    name: shortenEndpoint(s.endpoint, s.provider),
    completed: s.jobsCompleted,
    failed: s.jobsFailed,
    successRate: s.successRate,
    totalCalls: s.totalCalls,
  }));

  const hasData = data.length > 0;

  return (
    <ChartPanel
      title={title}
      iconClassName="bg-purple-50 text-purple-600"
      icon={
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      }
      summary={
        <div className="flex items-center gap-3 text-[10px] text-slate-500">
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-success-500" />
            completed
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-danger-500" />
            failed
          </span>
        </div>
      }
    >

      {hasData ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 25 }}>
              <defs>
                <ChartBarGradient
                  id={completedGradientId}
                  color={CHART_SUCCESS}
                  lightColor={CHART_SUCCESS_LIGHT}
                />
                <ChartBarGradient id={failedGradientId} color={CHART_ERROR} />
              </defs>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis
                {...CHART_AXIS_PROPS}
                dataKey="name"
                tick={CHART_AXIS_TICK}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis {...CHART_AXIS_PROPS} tick={CHART_AXIS_TICK} allowDecimals={false} width={30} />
              <Tooltip
                contentStyle={CHART_TOOLTIP_STYLE}
                labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                formatter={(value: number, name: string) => {
                  const displayName = name === 'completed' ? 'Completed Jobs' : 'Failed Jobs';
                  return [`${value}`, displayName];
                }}
                labelFormatter={(label, payload) => {
                  if (payload && payload.length > 0) {
                    const item = payload[0].payload as (typeof data)[number];
                    return `${label} — ${item.successRate.toFixed(1)}% success (${item.totalCalls} API calls)`;
                  }
                  return String(label);
                }}
              />
              <Bar
                {...CHART_BAR_PROPS}
                dataKey="completed"
                stackId="a"
                fill={chartGradientFill(completedGradientId)}
                radius={[0, 0, 0, 0]}
                isAnimationActive={false}
              />
              <Bar
                {...CHART_BAR_PROPS}
                dataKey="failed"
                stackId="a"
                fill={chartGradientFill(failedGradientId)}
                radius={[3, 3, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <ChartEmptyState>No endpoint usage data available</ChartEmptyState>
      )}
    </ChartPanel>
  );
}
